import React, { useEffect, useMemo, useState } from "react";
import { Box, useDisclosure, useTheme, useMediaQuery } from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { menuTypeTheme } from "../helpers/values";
import { isLightColor } from "../helpers/utils";
import {
  BottomNavBar,
  BottomNavActiveId,
  usePubConfig,
  usePub,
  useCustomerOptional,
  VenueReservationDrawer,
} from "@zillout/shared-components";
import { env } from "../config/env";
import { appendTrackingParams } from "../hooks/useTrackingSource";
import { ZILLOUT_WEB_BASE_URL } from "../config/urls";

interface BottomNavLayoutProps {
  children: React.ReactNode;
  pubId?: string;
}

export const BottomNavLayout: React.FC<BottomNavLayoutProps> = ({ children, pubId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [isMobile] = useMediaQuery("(max-width: 767px)");

  const placeId = pubId || env.REACT_APP_PLACE_ID;

  const theme = useTheme();
  const defaultColors = theme.colors["dark"];
  const [pubColors, setPubColors] = useState(defaultColors);

  const { data: pubConfig } = usePubConfig(placeId);
  const { data: pub, isLoading: isPubLoading } = usePub(placeId);

  const pubReady = !isPubLoading ;
  const dayEvent = pub?.data?.day_event;
  const isMenuEnabled = pub?.data?.pub_detail?.isMenuEnabled;
  const todayEvent = pub?.data?.event_res;
  const firstEvent = todayEvent  || null;

  const customer = useCustomerOptional();
  const isLoggedIn = !!customer?.customerData?.id;

  useEffect(() => {
    if (pubConfig?.data) {
      const { menuType, menuColors } = pubConfig.data;
      const themeKey = menuTypeTheme[menuType]?.theme || "light";
      const fallbackTheme = theme.colors[themeKey];
      const menuTheme = menuTypeTheme[menuType] || {};

      const colors = {
        primary:
          menuColors?.secondaryContainer &&
          menuColors.secondaryContainer !== "#000000"
            ? menuColors.secondaryContainer
            : menuTheme.primary || fallbackTheme.primary,

        secondary:
          menuColors?.onPrimaryContainer &&
          menuColors.onPrimaryContainer !== "#000000"
            ? menuColors.onPrimaryContainer
            : menuTheme.secondary || fallbackTheme.secondary,

        bg:
          menuColors?.onSecondaryContainer &&
          menuColors.onSecondaryContainer !== "#000000"
            ? menuColors.onSecondaryContainer
            : menuTheme.bg || fallbackTheme.bg,

        card_bg:
          menuColors?.menuItemCard && menuColors.menuItemCard !== "#000000"
            ? menuColors.menuItemCard
            : menuTheme.secondary || fallbackTheme.card_bg,

        text:
          menuColors?.text && menuColors.text !== "#000000"
            ? menuColors.text
            : menuTheme.text || fallbackTheme.text,

        icon_color: isLightColor(
          menuColors?.onSecondaryContainer &&
            menuColors.onSecondaryContainer !== "#000000"
            ? menuColors.onSecondaryContainer
            : menuTheme.bg || fallbackTheme.icon_color
        )
          ? "black"
          : "white",

        card_border: defaultColors.card_border || "#3A3D43",
      };

      setPubColors({
        ...fallbackTheme,
        ...colors,
      });
    }
  }, [pubConfig]);

  // Determine if navbar should show and what active state it should have
  const navbarState = useMemo(() => {
    const pathname = location.pathname;

    const navbarRoutes = [
      { pattern: /^\/$/, activeId: "home" as BottomNavActiveId },                          // navigate(`/`)
      { pattern: /^\/place\/[^/]+\/?$/, activeId: "home" as BottomNavActiveId },          // /place/:pub_id
      { pattern: /^\/pub\/detail\/[^/]+\/?$/, activeId: "home" as BottomNavActiveId },    
      { pattern: /^\/place\/[^/]+\/offers$/, activeId: "offers" as BottomNavActiveId },   // navigate(`/place/${placeId}/offers`)
      { pattern: /^\/event\/detail\//, activeId: "offers" as BottomNavActiveId },         // navigate(`/event/detail/${uuid}`)
      { pattern: /^\/gallery($|\/)/, activeId: "gallery" as BottomNavActiveId },          // navigate(`/gallery`)
      { pattern: /^\/account($|\/)/, activeId: "profile" as BottomNavActiveId },          // navigate("/account")
    ];

    for (const route of navbarRoutes) {
      if (route.pattern.test(pathname)) {
        return { show: true, activeId: route.activeId };
      }
    }

    return { show: false, activeId: null };
  }, [location.pathname]);

  const handleNavigation = (navId: string) => {
    switch (navId) {
      case "home":
        navigate(`/`);
        break;
      case "offers":
        if (firstEvent?.uuid) {
          navigate(`/pub/detail/events/${placeId}`);
        } else {
          navigate(`/place/${placeId}/offers`);
        }
        break;
      case "menuOrGallery":
        if (isMenuEnabled) {
          window.open(
            appendTrackingParams(`${ZILLOUT_WEB_BASE_URL}/pub/detail/menu/overview/${placeId}`),
            "_self"
          );
        } else {
          navigate(`/gallery`);
        }
        break;
      case "profile":
        if (isLoggedIn) {
          navigate("/account");
        } else {
          const currentPath = window.location.pathname + window.location.search;
          navigate(`/verify-phone/direct?redirect=${encodeURIComponent(currentPath)}`);
        }
        break;
    }
  };

  return (
    <Box>
      {children}

      {isMobile && navbarState.show && navbarState.activeId && pubReady && (
        <Box position="fixed" bottom={0} left={0} right={0} zIndex={40}>
          <Box display="flex" justifyContent="center" pb="max(env(safe-area-inset-bottom), 8px)">
            <BottomNavBar
              activeId={navbarState.activeId}
              onNavigate={{
                home: () => handleNavigation("home"),
                offers: () => handleNavigation("offers"),
              menuOrGallery: () => handleNavigation("menuOrGallery"),
              profile: () => handleNavigation("profile"),
              reserve: onOpen,
              }}
              showMenu={!!isMenuEnabled}
              showEvents={!!firstEvent}
              menuColors={pubColors as any}
              labels={{
                home: "Home",
                offers: "Offers",
                menu: "Menu",
                gallery: "Gallery",
                profile: "Profile",
                reserve: "Reserve",
              }}
              accentColor={pubConfig?.accentColor || "#7B61FF"}
              barColor={pubConfig?.barColor || "#1E1E24"}
            />
          </Box>
        </Box>
      )}

      {/* Reservation drawer - available on every page */}
      <VenueReservationDrawer
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        dayEvent={dayEvent}
        pubId={placeId}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        pubColors={pubColors as any}
        onDayEventNavigate={(path) => {
          onClose();
          setTimeout(() => {
            window.open(appendTrackingParams(`${ZILLOUT_WEB_BASE_URL}${path}`), "_self");
          }, 100);
        }}
        onContinue={() => {
          if (selectedDate && placeId) {
            const year = selectedDate.getFullYear();
            const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
            const day = String(selectedDate.getDate()).padStart(2, "0");
            const formattedDate = `${year}-${month}-${day}`;
            navigate(`/pub/detail/events/${placeId}?date=${formattedDate}`);
            onClose();
          }
        }}
      />
    </Box>
  );
};

export default BottomNavLayout;
